import {Component, Inject, LOCALE_ID} from '@angular/core';
import {Role} from '../../../model/auth/Role';
import {MobileAwareComponent} from '../../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../../model/contest/Contest';
import {Region} from '../../../model/location/Region';
import {AuthService} from '../../../service/auth.service';
import {TeacherExamStats} from '../../../model/stats/Stats';
import {NotificationHandler} from '../../../helper/notification.handler';
import {StatsService} from '../../../service/stats.service';
import {DEFAULT_PAGE, Page} from '../../../model/Page';
import {School, SchoolType} from '../../../model/location/School';
import {User} from '../../../model/auth/User';
import {LocationService} from '../../../service/location.service';
import {TenantService} from '../../../service/tenant.service';
import {Locale} from '../../../model/Locale';
import {Session} from '../../../model/auth/Session';

@Component({
  selector: 'app-teacher-result-stats',
  templateUrl: './teacher-results-stats.component.html',
  styleUrls: ['./teacher-results-stats.component.scss']
})
export class TeacherResultsStatsComponent extends MobileAwareComponent {

  private readonly PAGE_SIZE = 20;
  readonly roles = [Role.Host, Role.Coordinator];

  competitionId?: string;

  user?: User | null = null;
  session?: Session;
  callerRegion?: Region;

  contest?: Contest;

  region?: Region;
  schoolType?: SchoolType;
  school?: School;

  regions: Map<string, string> = new Map<string, string>();
  teacherStats: TeacherExamStats[] | null = null;

  inProgress = false;

  page: Page<never> = DEFAULT_PAGE;

  constructor(private statsService: StatsService,
              private locationService: LocationService,
              private authService: AuthService,
              private tenantService: TenantService,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  async initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    this.loadRegions();

    const user = await this.authService.getUser();
    if (user) {
      if (user.region) {
        this.region = new Region(user.region, '');
        this.callerRegion = new Region(user.region, '');
      } else {
        this.region = new Region('-1', '');
      }
    }

    this.user = user;
    this.session = await this.authService.getSession();
  }

  private loadRegions(): void {
    this.locationService.getRegions(this.locale as Locale).subscribe(regions => {
      regions.forEach(region => this.regions.set(region.id, region.name));
    });
  }

  loadTeacherStats(contest?: Contest, region?: Region, school?: School, reset = false) {
    if (!this.competitionId || !contest) return;

    if (reset) {
      this.page = {
        max_items: this.PAGE_SIZE,
        asc: false
      };
      this.teacherStats = null;
    }

    this.inProgress = true;

    const schoolId = region ? school?.id : undefined;
    const regionId = region && region.id != '-1' ? region.id : undefined;
    this.statsService.getTeacherExamStats(this.competitionId, contest.id, regionId, schoolId, this.page).subscribe(page => {
      const data = page.content || [];
      if (reset) {
        this.teacherStats = data;
      } else {
        if (!this.teacherStats) {
          this.teacherStats = data;
        } else {
          this.teacherStats = this.teacherStats.concat(data);
        }
      }

      this.inProgress = false;

      this.page.next_token = page.next_token;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  regionSelected(region: Region) {
    const regionChanged = this.region != region;
    this.region = region;

    if (regionChanged) this.loadTeacherStats(this.contest, this.region, this.school, true);
  }

  schoolSelected(school?: School, region?: Region) {
    const schoolChanged = this.school != school;
    this.school = school;
    if (schoolChanged) this.region = region;

    if (this.region && schoolChanged) {
      this.loadTeacherStats(this.contest, this.region, this.school, true);
    }
  }

  contestSelected(contest: Contest) {
    this.contest = contest;

    this.loadTeacherStats(this.contest, this.region, this.school, true);
  }

  loadNextPage() {
    this.loadTeacherStats(this.contest, this.region, this.school);
  }

}
